import {
    Box,
    TextField,
    Typography,
    styled,
    Snackbar,
    Alert,
    Button,
    Paper,
    Tooltip,
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import { ArrowBack, ManageSearch, Error as ErrorIcon } from '@mui/icons-material';
import { mutateForcedRankingStrategy } from 'src/mutations/mutateForcedRankingStrategy';
import { FORCED_RANKING_SEARCH } from 'src/lib/routes';
import { useNavigate } from 'react-router-dom';
import { useDeleteForcedRankingStrategy } from 'src/hooks/useDeleteForcedRankingStrategy';
import { getUserEmailQuery } from 'src/selectors/getUserEmailQuery';
import { SaveLoadingButton } from '../common/buttons/SaveLoadingButton';
import { CancelLoadingButton } from '../common/buttons/CancelLoadingButton';
import { TopBarActions } from './TopBarActions';
import { RankingTabs } from './RankingTabs';
import { RankingTable } from './RankingTable';
import { useStrategy } from '../../hooks/useStrategy';
import { ManageOptionGroupsDialog } from './ManageOptionGroupsDialog';
import { Loader } from '../common/Loader';
import { validateStrategyName } from './utils';

interface DesignConceptForcedRankingDetailsProps {
    strategy: Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy;
}

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(16),
}));

const Footer = styled(Paper)(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1.5),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 0,
    zIndex: theme.zIndex.appBar,
}));

export const ForcedRankingDetails = ({
    strategy: initialStrategy,
}: DesignConceptForcedRankingDetailsProps): JSX.Element => {
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(0);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [optionGroupsDialogOpen, setOptionGroupsDialogOpen] = useState(false);
    const [nameValidationError, setNameValidationError] = useState<string | null>(null);
    const accessToken = useRecoilValue(isAuthorizedQuery) as string;
    const navigate = useNavigate();
    const deleteStrategy = useDeleteForcedRankingStrategy({ useErrorBoundary: false });
    const userEmail = useRecoilValue(getUserEmailQuery);
    const [showAddRow, setShowAddRow] = useState(false);
    const {
        strategy,
        setStrategy,
        updateRanking,
        updateName,
        updateReferenceProduct,
        addOptionGroup,
        updateOptionGroup,
        deleteOptionGroup,
        hasUnsavedChanges,
        discardChanges,
        markAsSaved,
        updateVariantCultures,
        addVariant,
        deleteVariant,
        importRanksFromCsv,
        addRankToVariant,
        deleteRankFromVariant,
        reorderRankings,
        updateBulkFilteringOptionGroup,
        updateBulkDefaultOptionGroup,
        deleteBulkRankings,
        cloneVariant,
    } = useStrategy(initialStrategy);
    const hasEmptyVariant = strategy?.cultureVariantRankings.some(
        (variant) => variant.cultures.length === 0 && variant.rankings.length === 0,
    );

    const duplicateRankingsMap = useMemo(() => {
        const duplicates = new Map<number, number[]>();

        if (!strategy || !strategy.cultureVariantRankings[selectedVariant]) {
            return duplicates;
        }

        const { rankings } = strategy.cultureVariantRankings[selectedVariant];
        const keyToIndices = new Map<string, number[]>();

        rankings.forEach((ranking, index) => {
            const key = `${ranking.designConceptId}|${ranking.filteringProductSpecId ?? ''}|${ranking.defaultProductSpecId ?? ''}`;
            const indices = keyToIndices.get(key) ?? [];

            indices.push(index);
            keyToIndices.set(key, indices);
        });

        keyToIndices.forEach((indices) => {
            if (indices.length > 1) {
                indices.forEach((index) => {
                    const otherRanks = indices
                        .filter((i) => i !== index)
                        .map((i) => rankings[i].rank);

                    duplicates.set(index, otherRanks);
                });
            }
        });

        return duplicates;
    }, [strategy, selectedVariant]);

    const hasDuplicateDesignConcepts = duplicateRankingsMap.size > 0;

    useEffect(() => {
        if (strategy && strategy.cultureVariantRankings.length === 0) {
            addVariant();
        }
    }, [strategy, addVariant]);

    const mutation = useMutation({
        mutationFn: mutateForcedRankingStrategy,
        onSuccess: (result) => {
            setShowSuccessSnackbar(true);
            markAsSaved();
            setStrategy(result);
            // Update the URL if the strategy name has changed
            if (result.name !== initialStrategy.name) {
                navigate(`/design-concept-forced-ranking/${result.name}`, { replace: true });
            }
        },
        onError: (error: Error) => {
            console.error('Failed to save strategy:', error);
        },
    });

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
            if (hasUnsavedChanges) {
                const message = 'You have unsaved changes. Are you sure you want to leave?';

                e.preventDefault();
                e.returnValue = message;
                return message;
            }
            return undefined;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    const handleCancel = (): void => {
        discardChanges();
    };

    const handleSave = async (): Promise<void> => {
        if (!strategy) {
            return;
        }

        await mutation.mutateAsync({
            accessToken,
            strategyName: initialStrategy.name,
            data: {
                ...strategy,
                modifiedBy: userEmail,
            },
        }, {
            onSuccess: (result) => {
                navigate(`/design-concept-forced-ranking/${result.name}`, { replace: true });
            },
        });
    };

    const handleDelete = async (): Promise<void> => {
        await deleteStrategy.mutateAsync({
            accessToken,
            strategyName: initialStrategy.name,
        });
    };

    const handleRowSelect = (rankingIndex: number, selected: boolean): void => {
        if (selected) {
            setSelectedRows([...selectedRows, rankingIndex]);
        } else {
            setSelectedRows(selectedRows.filter((index) => index !== rankingIndex));
        }
    };

    const handleSelectAll = (selected: boolean): void => {
        if (!strategy) return;

        if (selected) {
            const allIndices = strategy.cultureVariantRankings[selectedVariant].rankings
                .map((_, index) => index);

            setSelectedRows(allIndices);
        } else {
            setSelectedRows([]);
        }
    };

    const handleVariantChange = (variantIndex: number): void => {
        setSelectedVariant(variantIndex);
        setSelectedRows([]);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.target.value;
        const validationError = validateStrategyName(newValue);

        updateName(newValue);

        // Validate the name
        setNameValidationError(validationError);
    };

    const handleReferenceProductChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        updateReferenceProduct(event.target.value);
    };

    const handleFilteringOptionChange = (rankingIndex: number, specId: string | null): void => {
        if (!strategy) {
            return;
        }

        if (selectedRows.length > 1 && selectedRows.includes(rankingIndex)) {
            const selectedRankings = selectedRows.map(
                (index) => strategy.cultureVariantRankings[selectedVariant].rankings[index],
            );

            updateBulkFilteringOptionGroup(selectedVariant, selectedRankings, specId);
            return;
        }

        updateRanking(
            selectedVariant,
            rankingIndex,
            { filteringProductSpecId: specId },
        );
    };

    const handleDefaultOptionChange = (rankingIndex: number, specId: string | null): void => {
        if (!strategy) {
            return;
        }

        if (selectedRows.length > 1 && selectedRows.includes(rankingIndex)) {
            const selectedRankings = selectedRows.map(
                (index) => strategy.cultureVariantRankings[selectedVariant].rankings[index],
            );

            updateBulkDefaultOptionGroup(selectedVariant, selectedRankings, specId);
            return;
        }

        updateRanking(
            selectedVariant,
            rankingIndex,
            { defaultProductSpecId: specId },
        );
    };

    const handleManageOptionGroups = (): void => {
        setOptionGroupsDialogOpen(true);
    };

    const handleNavigation = (path: string): void => {
        if (hasUnsavedChanges) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');

            if (confirmed) {
                navigate(path);
            }
        } else {
            navigate(path);
        }
    };

    if (!strategy) {
        return <Loader />;
    }

    const handleVariantAdd = (): void => {
        addVariant();
        setSelectedVariant(strategy.cultureVariantRankings.length);
    };

    const handleReorderRankings = (oldIndex: number, newIndex: number): void => {
        reorderRankings(selectedVariant, oldIndex, newIndex);
    };

    const handleDeleteRanking = (rankingIndex: number): void => {
        if (selectedRows.length > 1 && selectedRows.includes(rankingIndex)) {
            const selectedRankings = selectedRows.map(
                (index) => strategy.cultureVariantRankings[selectedVariant].rankings[index],
            );

            deleteBulkRankings(selectedVariant, selectedRankings);
            setSelectedRows([]);
            return;
        }

        const ranking = strategy.cultureVariantRankings[selectedVariant].rankings[rankingIndex];

        deleteRankFromVariant(selectedVariant, ranking);
    };

    return (
        <>
            <TitledAppBar title={(
                <Typography variant="h6">
                    {initialStrategy.name}
                    {' '}
                    Forced Ranking
                </Typography>
                )}
            >
                <TopBarActions
                    selectedVariantIndex={selectedVariant}
                    strategy={initialStrategy}
                    onDelete={handleDelete}
                />
            </TitledAppBar>
            <StyledContainer>
                <Button
                    color="secondary"
                    size="small"
                    startIcon={<ArrowBack />}
                    sx={{
                        justifySelf: 'flex-start',
                        width: 'fit-content',
                    }}
                    variant="text"
                    onClick={(): void => handleNavigation(FORCED_RANKING_SEARCH)}
                >
                    Back to Forced Rankings
                </Button>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                }}
                >
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                    }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                error={!!nameValidationError}
                                helperText={nameValidationError}
                                label="Ranking Strategy Name"
                                size="small"
                                sx={{ width: '400px' }}
                                value={strategy.name}
                                onChange={handleNameChange}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                label="Reference PRD"
                                size="small"
                                value={strategy.referenceProduct}
                                onChange={handleReferenceProductChange}
                            />
                        </Box>
                    </Box>
                    <Button
                        startIcon={<ManageSearch />}
                        variant="contained"
                        onClick={handleManageOptionGroups}
                    >
                        Manage option groups
                    </Button>
                </Box>
                {strategy.cultureVariantRankings?.length > 0 && strategy.cultureVariantRankings[selectedVariant] && (
                    <>
                        <RankingTabs
                            disableAddVariant={hasEmptyVariant}
                            selectedVariant={selectedVariant}
                            variants={strategy.cultureVariantRankings}
                            onShowAddRow={(): void => setShowAddRow(true)}
                            onUpdateVariantCultures={updateVariantCultures}
                            onVariantAdd={handleVariantAdd}
                            onVariantChange={handleVariantChange}
                            onVariantClone={(index): void => {
                                cloneVariant(index);
                                setSelectedVariant(strategy.cultureVariantRankings.length);
                            }}
                            onVariantDelete={(): void => {
                                deleteVariant(selectedVariant);
                                setSelectedVariant(0);
                            }}
                        />
                        <RankingTable
                            cultures={strategy.cultureVariantRankings[selectedVariant].cultures}
                            defaultPartialProductSpecs={strategy.defaultPartialProductSpecs ?? {}}
                            duplicateRankingsMap={duplicateRankingsMap}
                            filteringPartialProductSpecs={strategy.filteringPartialProductSpecs ?? {}}
                            hasCultureSelected={strategy.cultureVariantRankings[selectedVariant].cultures.length > 0}
                            productKey={strategy.referenceProduct}
                            rankings={strategy.cultureVariantRankings[selectedVariant].rankings}
                            selectedRows={selectedRows}
                            showAddRow={showAddRow}
                            onAddRanking={(ranking): void => addRankToVariant(selectedVariant, ranking)}
                            onDefaultOptionChange={handleDefaultOptionChange}
                            onDeleteRanking={handleDeleteRanking}
                            onFilteringOptionChange={handleFilteringOptionChange}
                            onHideAddRow={(): void => setShowAddRow(false)}
                            onImportCsv={(content): Promise<void> => importRanksFromCsv(selectedVariant, content)}
                            onReorderRankings={handleReorderRankings}
                            onRowSelect={handleRowSelect}
                            onSelectAll={handleSelectAll}
                            onShowAddRow={(): void => setShowAddRow(true)}
                        />
                    </>
                )}
            </StyledContainer>
            <Footer elevation={3} sx={{ padding: 4 }}>
                <Typography variant="caption">
                    {`Last modified by ${strategy.modifiedBy} on ${new Date(strategy.modifiedDate).toLocaleString()}`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <CancelLoadingButton
                        disabled={mutation.isLoading || !hasUnsavedChanges}
                        onClick={handleCancel}
                    >
                        Cancel
                    </CancelLoadingButton>
                    {hasDuplicateDesignConcepts && (
                        <Tooltip title="Duplicate design concepts with the same option groups are not allowed">
                            <ErrorIcon color="error" />
                        </Tooltip>
                    )}
                    <Tooltip
                        title={hasDuplicateDesignConcepts
                            ? 'Duplicate design concepts with the same option groups are not allowed'
                            : ''}
                    >
                        <span>
                            <SaveLoadingButton
                                disabled={!hasUnsavedChanges || !!nameValidationError || hasDuplicateDesignConcepts}
                                variant="contained"
                                onClick={handleSave}
                            >
                                Save
                            </SaveLoadingButton>
                        </span>
                    </Tooltip>
                </Box>
            </Footer>
            <ManageOptionGroupsDialog
                addOptionGroup={addOptionGroup}
                defaultPartialProductSpecs={strategy.defaultPartialProductSpecs}
                deleteOptionGroup={deleteOptionGroup}
                filteringPartialProductSpecs={strategy.filteringPartialProductSpecs}
                open={optionGroupsDialogOpen}
                productKey={strategy.referenceProduct}
                updateOptionGroup={updateOptionGroup}
                onClose={(): void => setOptionGroupsDialogOpen(false)}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={showSuccessSnackbar}
                onClose={(): void => setShowSuccessSnackbar(false)}
            >
                <Alert severity="success" variant="filled">
                    Strategy saved successfully
                </Alert>
            </Snackbar>
        </>
    );
};
